// src/lib/blogSampleRequest.ts
//
// BLOG-LEAD-MAGNET-SAMPLES-001 — browser helper for the blog "free 15-page
// sample" form. Posts to the `blog-sample-request` edge function with the
// public anon key, the same raw-fetch pattern as src/lib/contactSubmit.ts.
//
// Privacy: the address and the ESA/PSD choice travel ONLY in this POST body.
// Nothing is written to the URL, storage, analytics, pixels or the attribution
// store, and no Lead/conversion event is fired.

import { MARKETING_CONSENT, type SampleVariant } from "@/data/blogSampleOffer";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export type SampleRequestOutcome = "sent" | "invalid_email" | "rate_limited" | "failed";

export async function requestBlogSample(input: {
  email: string;
  variant: SampleVariant;
  sourcePath: string;
  marketingConsent: boolean;
  /** Honeypot value — always "" for a real visitor. */
  website: string;
}): Promise<SampleRequestOutcome> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return "failed";

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 25000);
  try {
    const res = await fetch(`${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/blog-sample-request`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        email: input.email.trim(),
        variant: input.variant,
        sourcePath: input.sourcePath,
        marketingConsent: input.marketingConsent,
        ...(input.marketingConsent ? { consentVersion: MARKETING_CONSENT.version } : {}),
        website: input.website,
      }),
      signal: controller.signal,
    });
    let data: { ok?: boolean; code?: string } = {};
    try {
      data = await res.json();
    } catch {
      /* non-JSON answer */
    }
    if (res.ok && data.ok) return "sent";
    if (data.code === "invalid_email") return "invalid_email";
    if (res.status === 429 || data.code === "rate_limited") return "rate_limited";
    return "failed";
  } catch {
    return "failed";
  } finally {
    clearTimeout(timer);
  }
}
